import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Icon, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { toast } from 'react-toastify';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import {
  deleteTrainingPartFile,
  downloadTrainingPartFile,
  getTrainingPartFilesList,
  openTrainingPartFile,
  uploadTrainingPartFile,
  updateTrainingPartFileVisibility,
} from 'http/trainingPartFiles';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig, resolveApiErrorMessage, transformTableData } from 'utils';
import getTrainingPartFilesTableConfig from './helpers/getTrainingPartFilesTableConfig';

const ACCEPTED_FILE_FORMAT = '.pdf, .doc, .docx, .ppt, .pptx, .xlsx, .jpeg, .jpg, .png';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  hiddenInput: {
    display: 'none',
  },
}));

const TrainingPartFilesTable = ({
  trainingPartId,
  canAddFiles = true,
  canDeleteFiles = true,
  canManageVisibility = true,
  isReadOnly = false,
  isSystemModule = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);

  const closeModal = useCallback(() => dispatch(modalActions.closeModal()), [dispatch]);

  const [filesState, fetchFiles] = useAsyncFn(async () => {
    return getTrainingPartFilesList(trainingPartId, {
      page: 0,
      pageSize: 100,
    });
  }, [trainingPartId]);

  const refreshFiles = useCallback(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (trainingPartId) {
      fetchFiles();
    }
  }, [fetchFiles, trainingPartId]);

  useEffect(() => {
    if (!filesState.value?.error && !filesState.value?.statusCode) {
      setFiles(
        (filesState.value?.items || []).map(item => ({
          ...item,
          visibility: {
            id: item.id,
            isVisibleForStudent: item.isVisibleForStudent,
            isVisibleForTrainer: item.isVisibleForTrainer,
            isDisabled: isSystemModule && item.source === 'system',
          },
        })),
      );
    }
  }, [filesState.value, isSystemModule]);

  const showError = useCallback(
    message => {
      toast.error(
        resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      );
    },
    [t],
  );

  const [visibilityState, updateVisibility] = useAsyncFn(
    async (fileId, data) => {
      const result = await updateTrainingPartFileVisibility(trainingPartId, fileId, data);
      if (!result?.error && !result?.statusCode) {
        refreshFiles();
        return result;
      }
      showError(result?.message);
      return result;
    },
    [refreshFiles, showError, trainingPartId],
  );

  const handleToggleVisibility = useCallback(
    (fileId, data) => {
      if (isReadOnly || !canManageVisibility) {
        return;
      }
      updateVisibility(fileId, data);
    },
    [canManageVisibility, isReadOnly, updateVisibility],
  );

  const tableConfig = useMemo(
    () =>
      getTrainingPartFilesTableConfig({
        showVisibility: canManageVisibility,
        handleToggleVisibility,
        isReadOnly: isReadOnly || !canManageVisibility,
      }),
    [canManageVisibility, handleToggleVisibility, isReadOnly],
  );

  const [uploadState, uploadFile] = useAsyncFn(
    async file => {
      const result = await uploadTrainingPartFile(trainingPartId, file);
      if (!result?.error && !result?.statusCode) {
        refreshFiles();
        return result;
      }
      showError(result?.message);
      return result;
    },
    [refreshFiles, showError, trainingPartId],
  );

  const [deleteState, removeFile] = useAsyncFn(
    async fileId => {
      const result = await deleteTrainingPartFile(trainingPartId, fileId);
      if (!result?.error && !result?.statusCode) {
        closeModal();
        refreshFiles();
        return result;
      }
      showError(result?.message);
      return result;
    },
    [closeModal, refreshFiles, showError, trainingPartId],
  );

  const [actionState, runFileAction] = useAsyncFn(async ({ action, fileId, fileName }) => {
    try {
      if (action === 'view') {
        await openTrainingPartFile(trainingPartId, fileId, fileName);
      } else {
        await downloadTrainingPartFile(trainingPartId, fileId, fileName);
      }
      return { success: true };
    } catch (error) {
      showError(error?.message);
      return error;
    }
  });

  const handleAddClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = event => {
    const file = event.target.files?.[0];
    if (file) {
      uploadFile(file);
    }
  };

  const handleDeleteFile = useCallback(
    (fileId, fileName) => {
      const [config, element] = getConfirmationModalConfig({
        title: t('trainingPartFiles.deleteModalTitle'),
        confirmAction: () => removeFile(fileId),
        rejectAction: closeModal,
        message: t('trainingPartFiles.deleteConfirmationMessage', { fileName }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, removeFile, t],
  );

  const tableActions = useMemo(() => {
    const actions = [
      {
        type: 'icon',
        icon: 'visibility',
        title: t('trainingPartFiles.actionView'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            runFileAction({ action: 'view', fileId, fileName: file.name });
          }
        },
      },
      {
        type: 'icon',
        icon: 'get_app',
        title: t('trainingPartFiles.actionDownload'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            runFileAction({ action: 'download', fileId, fileName: file.name });
          }
        },
      },
    ];

    if (canDeleteFiles) {
      actions.push({
        type: 'icon',
        icon: 'delete',
        color: 'error',
        title: t('trainingPartFiles.actionDelete'),
        onClick: event => {
          const fileId = Number(event.currentTarget.dataset.key);
          const file = files.find(item => item.id === fileId);
          if (file) {
            handleDeleteFile(fileId, file.name);
          }
        },
        getHidden: rowData => {
          if (!isSystemModule) {
            return false;
          }
          const file = files.find(item => item.id === rowData?.id);
          return file?.source === 'system';
        },
      });
    }

    return actions;
  }, [canDeleteFiles, files, handleDeleteFile, isSystemModule, runFileAction, t]);

  const [columns, rows] = transformTableData(tableConfig, files);
  const isLoading =
    filesState.loading || uploadState.loading || deleteState.loading || actionState.loading || visibilityState.loading;

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.sectionTitle}>
          {t('trainingPartFiles.sectionTitle')}
        </Typography>
        {canAddFiles ? (
          <>
            <Button color="primary" variant="contained" startIcon={<Icon>add</Icon>} onClick={handleAddClick}>
              {t('trainingPartFiles.actionAdd')}
            </Button>
            <input
              ref={fileInputRef}
              className={classes.hiddenInput}
              type="file"
              accept={ACCEPTED_FILE_FORMAT}
              onChange={handleFileSelected}
            />
          </>
        ) : null}
      </Box>
      <ModalBusyContainer busy={isLoading}>
        <AppTable
          columns={columns}
          data={rows}
          totalCount={files.length}
          rowsPerPage={files.length || 10}
          actions={tableActions}
        />
      </ModalBusyContainer>
    </Box>
  );
};

TrainingPartFilesTable.propTypes = {
  trainingPartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  canAddFiles: PropTypes.bool,
  canDeleteFiles: PropTypes.bool,
  canManageVisibility: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isSystemModule: PropTypes.bool,
};

export default TrainingPartFilesTable;
